// client/src/coms/e2ee/EncryptedMessageView.js
// How an end-to-end encrypted message looks on this device, shared by server channels and DMs. A message this
// device cannot decrypt is never shown as empty or left out: it stays in the list (sender, time, position) with a
// placeholder and a "Verify device" action. The placeholder text is also the message's text for quoting, copying and
// local search, so nothing readable leaks into those either.
import React from 'react';
import { requestDeviceVerification } from './cryptoSession';
import './e2ee.css';

export const LOCKED_TEXT = '🔒 Encrypted message';
export const DECRYPTING_TEXT = '🔒 Decrypting…';
export const FAILED_AUTH_TEXT = '⚠️ This encrypted message failed an authenticity check and is not shown.';

// r: the decryption result from cryptoSession (or null while it is pending).
// -> { kind: 'decrypting' | 'ok' | 'restored' | 'locked' | 'failed_auth', body, code }
export function encryptedView(r) {
  if (!r) return { kind: 'decrypting', body: DECRYPTING_TEXT, code: '' };
  if (r.ok) return { kind: r.fromBackup ? 'restored' : 'ok', body: r.body, code: '' };
  if (r.code === 'sender_mismatch' || r.code === 'replay') return { kind: 'failed_auth', body: FAILED_AUTH_TEXT, code: r.code };
  return { kind: 'locked', body: LOCKED_TEXT, code: r.code || '' };
}

// A message row with e2ee_view attached: { ...row, body/content: view.body, e2ee_view: view }.
export function withEncryptedView(row, r, textField = 'body') {
  const view = encryptedView(r);
  return { ...row, [textField]: view.body, e2ee_view: view };
}

export function EncryptedPlaceholder({ view }) {
  if (view.kind === 'decrypting') {
    return (
      <span className="vx-e2ee-msg is-pending" data-testid="e2ee-msg-decrypting">
        {DECRYPTING_TEXT}
      </span>
    );
  }
  if (view.kind === 'failed_auth') {
    return (
      <span className="vx-e2ee-msg is-bad" role="note" data-testid="e2ee-msg-failed">
        {FAILED_AUTH_TEXT}
      </span>
    );
  }
  return (
    <span className="vx-e2ee-msg" role="note" data-testid="e2ee-msg-locked" data-e2ee-code={view.code}>
      <strong>{LOCKED_TEXT}</strong>
      <span>This device cannot decrypt this message.</span>
      <button type="button" className="vx-btn small" onClick={() => requestDeviceVerification()} data-testid="e2ee-msg-verify">
        Verify device
      </button>
    </span>
  );
}

export function RestoredNote() {
  return (
    <span className="vx-e2ee-restored" title="This message's key came from your key backup, which cannot prove who created it." data-testid="e2ee-msg-restored">
      Restored from your key backup · sender not confirmed on this device
    </span>
  );
}
